/** @jsxImportSource minimal-view */

import { Deferred } from 'everyday-utils'
import { Rect, clamp } from 'geometrik'
import { element, on, queue, view, web } from 'minimal-view'
import { PFFT } from 'pretty-fast-fft'
import { Build, Caller, DsplNode, Info, Param, RmsService, VAudio, VScalar, createRms, preludeTokens, produce } from '../vm/interface'
import { CstNode, Token, bounds, differ, parse, tokenize } from '../vm/interface/lang'
import { parseNumber } from '../vm/interface/util'
import { anim } from './anim'
import { app } from './app'
import { audio } from './audio'
import { PLOT_HEIGHT_PX, PLOT_WIDTH_PX } from './constants'
import { debugDraw } from './debug-draw'
import { Deco, Dim, Editor, LineCol, Marker, Sub } from './editor/editor'
import { IS_MOBILE } from './env'
import { ErrorMarker } from './error-marker'
import { ErrorSub } from './error-sub'
import { hmr } from './hmr'
import { Knob } from './knob'
import { PickMarker } from './pick-marker'
import { Plot } from './plot'
import { Rms } from './rms'
import { screen } from './screen'
import { skin } from './skin'
import { copyRingInto } from './util/get-ring-offsets'
// import { User, userId, ws } from './ws'
// import { Lexicon } from './lexicon'
// import { grid } from './grid'

// async function rest() {
//   await Promise.resolve()
//   await Promise.resolve()
// }

/** todo
 *
 * - draw only visible plots
 *
 * - draw inlay hints in the editor
 *   - inferred labels and such, hz:
 *
 * - procedures: vol { 0 100 1 \ 100 / * }
 *   usage: 50 vol
 *
 * - numeric abbreviations: 1k 44k1 2k2
 *   - hex: 0xff or #ff
 *
 * - notes as variables: C B E F F# Fb
 *
 * - scales:
 *
 * - all math
 *   - bitwise/gates math for audio
 *
 * - variables: start with
 *   - global builtin: ar kr pi
 *
 * - collections
 *
 *   [sin 50 100 200 400 1 4 / on]
 *   [sin 50 100 200 400 splat]
 *
 *   50 100 200 400 {:x [sin x]}
 *
 *   50 100 200 400 { x [sin x] }
 *   50 100 { x 200 400 { y [sin x y] } }
 *
 *   50 200 100 400 map { :x :y [sin x y] }
 *   50 200 100 400 map { :x [sin x] }
 *
 *   0 1 10 .. (10 +)
 *
 * - operations on ranges
 *   - random ?
 *   - [doodle 1 4/]
 *
 * - capture and highlight what's in the stack at any cursor position
 *   - maybe a stack explorer on the right?
 *
 * - more gens/procedures: adr adsr
 *     some can be modelled as procedures tho, investigate
 */

// function rightBound(stack: CstItem[], x = 0) {
//   if (!stack) return x
//   let r: number
//   for (const n of stack) {
//     r = n.meta.tokens.right
//     if (r > x) x = r
//     if (n.stack) {
//       r = rightBound(n.stack)
//       if (r > x) x = r
//     }
//   }
//   return x
// }

export type SoundEditor = typeof SoundEditor.State
//[sin 785  \ [sin 5\]66   \*+] [exp 1] [lp 300]* @ .2  \* @LR+=
const defaultCodeValue = String.raw`[sin 300  \ [sin 1]42   \*+] [exp 1] [lp 300]* @ .2  \* @LR+=`
// const defaultCodeValue = `; fm
// ; --
// ;   Forth-like stack-based postfix notation synth.
// ;   Everything that remains in the stack in each block gets summed together.
// ;   Operations are postfix: 3 4 * 5 6 * === 42
// ;
// ; Available gens (so far):
// ;   sin, exp, lp, blp, bhp, bbp, bhs, bls, bpk, bno
// ;
// ; - Comments:
// ;   - Ctrl+; or Ctrl+/ : will create a line comment.
// ;   - Ctrl+Shift+; or Ctrl-Shift+/ :
// ;     The block that the editor highlights will become a block comment.
// ;     Block comments are nestable.
// ;     Examples of comments: (;1 2) [;sin] [;sin 12 (;34)]
// ;
// ; Advice: Play at low volume and careful when doing radical changes.
// ;   Don't comment out something for the first time without testing it at low volume first.
// ;   There are NO safety circuits yet implemented so the sound CAN and WILL explode!
// ; Reset by typing in DevTools console and then refresh: delete localStorage.codeValue
// ; If you disable live editing, you can use Ctrl+Enter to compile.
// (
// ([sin 50 160 [exp 2]*].15* [exp 2]* [lp 100] [dc .005] 2 *)
// ;--
// ([sin 245 300 [exp 3 [sin 3] .5* [lp 800]  ]*].7* [exp 2]*
//   [bbp 1500 1200 [sin 4]*,1] .38*
// [sin 220 289 [sin 1]* 50]1.4* [sin 20]* [sin 2]* [lp 200] [exp 2]* [dc .029] .45*)
// ;--
// (;[sin 314 500 [exp .5 [sin 11] .75*   ]* 2 [sin .7]*].7* [exp 2]*
//   [bbp 1220 1020 [sin 4]*,2] .38*
// [sin 1250 289 [sin 2424]* 50]1.4* [sin 172 50 [sin .2]*]* [sin 2]*
// [lp 1200] [exp 4]* .45*)
// ;--
// ([sin 910 (200) (300) [sin 2 [sin 1]1+*]100* +
// [sin 283][sin 2]10+ .4* * *].4* [sin 1]* [sin 1]* .17*
// [lp 1200 500 [sin 1]*] [sin .1]*)
// ;--
// ([sin 42 12 [sin 240]830* 180[sin .18]* 380[sin .08]*].7* [exp .25]* [blp 2500,4]
//  [sin 2]* .2* [sin 1.25]*)
// ;--
// ([sin 220 (1200) [sin 128 [sin 3] 10*] [sin 12] (200 (;50))*]
// [sin 200]*[sin 222]*.25 * [sin 3]* [sin 2]* [sin .1]* .3*)
// ;--
// ([sin 200 [sin [sin 3] 1*]1420* [sin 22]10*]
// [sin 22]*[sin 322]*.25 * [sin 4]* [sin 2]* [sin .1]* .14*)
// )
// ; [bhp 6000[exp .1]*,2]
// `

// export const paramUsers = new Map<string, Set<User>>()

export const SoundEditor = hmr(web(view('sound-editor',
  class props {
    name!: string
    live!: boolean
  },
  class local {
    host = element
    editor?: Editor
    dspl?: DsplNode
    rms?: RmsService
    code: string = localStorage.codeValue2 || defaultCodeValue

    info?: Info
    tokens?: Token[]
    build?: Build | null

    plots?: Plot<Dim<VAudio>>[]
    ranges?: Knob<Dim<Param>>[]
    rmss?: Rms[]
    tempPlotFloats = new Float32Array(2048)

    focusedAudios: VAudio[] = []

    shouldDraw = false
    // focusedAudiosHash: string = ''

    genDims?: Dim<VAudio>[]
    genDeco?: Deco[] = []

    rmsDims?: Dim<VAudio>[]
    rmsDeco?: Deco[] = []

    rangeDims?: Dim<Param>[]
    rangeDeco?: Deco[] = []
    rangeSubs?: Sub[] = []
    rangePlots?: Plot<Dim<Param>>[] = []

    // userDims?: Dim<User>[]
    // userSubs?: Sub[] = []

    debugDims?: Dim[]
    debugSubs?: Sub[] = []

    errors?: (Error & { cause: { nodes: CstNode[] } })[] = []
    errorSubs: Sub[] = []
    errorMarkers: Marker[] = []

    picks?: VScalar[] = []
    pickMarkers: Marker[] = []

    visible = new Set<string>()
    visibleHash: string = ''
    nextVisible = new Set<string>()

    // copy from editor so don't waste fx because they're hot
    line?: number
    col?: number
    brackets?: [LineCol, LineCol] | false

    isHandlingRange = false

    elevated?: LineCol[]

    namedRanges: Knob<Dim<Param>>[] = []

    analyser?: PFFT
  },
  function actions({ $, fn, fns }) {
    // let concurrent = 0
    // function getCodeChecksum() {
    //   return `${checksum($.editor!.$.code!.replace(/\d+/g, ''))}`
    // }

    // ws.onParamChange = queue.throttle(20)((user, chk, index, valueString) => {
    //   // TODO: actually check against all editor codes

    //   let code = $.editor!.$.code!

    //   const thisChk = getCodeChecksum()

    //   if (thisChk === chk) {
    //     const dim = $.rangeDims![index]
    //     const param = dim.meta!
    //     const current = param.target
    //     const prevLength = current.text.length

    //     code = code.slice(0, current.index)
    //       + valueString
    //       + code.slice(current.index + prevLength)

    //     // console.log(code)
    //     $.editor!.$.code = code

    //     // if (grid.$.selectedItem) grid.$.selectedItem.code = code

    //     user.param = index
    //   }
    // })

    return fns(new (class actions {
      handleRangeUpdate = (r: Knob, param: Param) => {
        // TODO: r.displayValue

        const format = param.target.format
        const digits = param.target.digits
        // format = 'k'
        // digits = b.length
        // value = Number(a) * 1000 + Number(b) * (1000 / (10 ** digits))

        const step = param.step
        // const fractionDigits = step.toString().split('.')?.[1]?.length ?? 0

        let scaled = ((r.value ** r.slope) * (r.max - r.min) + r.min)
        scaled = Math.max(r.min, Math.min(r.max, Math.floor(scaled / step) * step))

        let text = scaled.toFixed(digits)

        if (format === 'f') {
          // remove leading 0 for aesthetics + size reduction
          const [num, frac = ''] = text.split('.')
          if (num === '0' && frac.length) {
            text = '.' + frac
          }
        }
        else if (format === 'k') {
          const [num, frac = ''] = (scaled / 1000).toString().split('.')
          text = num + 'k' + (frac.slice(0, digits).padEnd(digits, '0'))
        }

        let code = $.editor!.$.code!

        // const chk = getCodeChecksum()
        // ws.send?.(`${userId},${app.$.userColor},${Lexicon.Param},${chk},${i},${text}`)

        const current = param.target
        const prevLength = current.text.length

        current.text = text
        current.value = r.value

        code = code.slice(0, current.index)
          + current.text
          + code.slice(current.index + prevLength)

        // console.log(code)
        // editor.$.draw()

        // const selectedItem = grid.$.selectedItem
        // if (selectedItem) selectedItem.code = code

        $.editor!.$.code = code

        r.slider?.$.draw()
      }

      handleRangeDown = (i: number) => (e: PointerEvent) => {
        $.isHandlingRange = true

        const r = $.ranges![i]
        const x = e.pageX
        const y = e.pageY
        const startValue = r.value
        r.active = true

        const onPointerMove = queue.raf((e: PointerEvent) => {
          const d = $.rangeDims![i]
          const param = d.meta!

          const width = (d.end! - d.start!) * $.editor!.$.charWidth
          const dx = e.pageX - x
          const dy = y - e.pageY
          const normal = dx / width + dy / width

          r.value = clamp(0, 1, (startValue + normal))

          this.handleRangeUpdate(r, param)
        })

        const off = on(window, 'pointermove').capture((e) => {
          e.stopPropagation()
          onPointerMove(e)
        })

        on(window, 'pointerup').once(() => {
          off()
          $.isHandlingRange = false
          r.active = false
          r.slider?.$.draw()
          // $.ranges = [...$.ranges!]
          requestAnimationFrame($.editor!.$.draw)
        })

        r.slider?.$.draw()
        $.editor!.$.draw()
      }

      handleRangeMove = (i: number) => (e: PointerEvent) => {
        if ($.isHandlingRange) return
        const r = $.ranges![i]
        if (!r.hover) {
          r.hover = true
          r.slider?.$.draw()
          $.editor!.$.draw()
        }
      }

      handleRangeLeave = (i: number) => () => {
        if ($.isHandlingRange) return
        const r = $.ranges![i]
        if (r.hover) {
          r.hover = false
          r.slider?.$.draw()
          $.editor!.$.draw()
        }
      }

      compile = async () => {
        const { dspl, rms, info } = $
        if (!dspl || !info || !rms) return

        const build = await dspl.frontend.build(info)
        if ('error' in build) {
          console.error(build.error)
          return
        }

        dspl.fetchSamples()

        if (build.skip) {
          // console.log('skipped')
          // $.visible.clear()
          $.shouldDraw = true
          $.build = build
          if (!dspl.running) {
            dspl.generatePreview(build)
          }
          this.maybeEmit()
          return
        }

        const deferred = Deferred<void>()

        // TODO: dspl.onError
        // TODO: what happens when this is flying and another comes

        dspl.onBuild = () => {
          requestAnimationFrame(() => {
            $.visible.clear()
            $.shouldDraw = true
            $.build = build
            deferred.resolve()
            if (!dspl.running) {
              dspl.generatePreview(build)
            }
            this.maybeEmit()
          })
        }

        dspl.onError = () => {
          deferred.resolve()
        }

        dspl.run(build)
        rms.setBuild(build)

        await deferred.promise
      }

      maybeEmit = () => {
        // const selectedItem = grid.$.selectedItem
        // if (!selectedItem) return

        // const posHash = `${[...Object.values(selectedItem.pos)].join(' ')}`
        // const code = $.code
        // if (code == null) return

        // const current = checksum(code)
        // const item = grid.$.itemsMap.get(posHash)
        // if (item) {
        //   const chk = checksum(item.code)
        //   const hasChanged = current !== chk
        //   if (hasChanged) {
        //     item.code = code
        //     ws.send?.(`${userId},${app.$.userColor},${Lexicon.Doc},${posHash},${$.editor!.$.line} ${$.editor!.$.col},${encodeURIComponent(code)}`)
        //   }
        // }
      }

      plot = () => {
        const { plots, rangePlots, tempPlotFloats, dspl } = $
        if (!plots || !dspl || !rangePlots) return

        let ringPos = dspl.frontend.buffers.ringPosBuffer[0]
        ringPos--
        if (ringPos < 0) ringPos = 15

        for (let i = 0,
          floats: Float32Array,
          dim: Dim<VAudio> | undefined,
          deco: Deco | undefined,
          plot: Plot | undefined
          ;
          i < plots.length;
          i++
        ) {
          plot = plots[i]

          dim = plot.meta
          if (!dim) continue

          deco = plot.deco!
          if (!deco.isVisible) continue

          plot.clear()

          floats = dim.meta!.floats!

          if (ringPos === 15) {
            plot.draw(floats)
          }
          else {
            copyRingInto(tempPlotFloats, floats, ringPos, 2048, 15)
            plot.draw(tempPlotFloats)
          }
        }

        for (let i = 0,
          floats: Float32Array,
          dim: Dim<Param> | undefined,
          deco: Deco | undefined,
          plot: Plot | undefined
          ;
          i < rangePlots.length;
          i++
        ) {
          plot = rangePlots[i]
          plot.clear()

          dim = plot.meta
          if (!dim) continue

          deco = plot.deco!
          if (!deco.isVisible) continue

          if (!dim?.meta?.last) continue

          floats = dim.meta.last.floats!

          if (ringPos === 15) {
            plot.draw(floats)
          }
          else {
            copyRingInto(tempPlotFloats, floats, ringPos, 2048, 15)
            plot.draw(tempPlotFloats)
          }
        }
      }

      calcRms = () => {
        $.rms!.run(0, 2048)
      }

      calcRmsSum = () => {
        audio.$.rmsSum = $.rmss?.reduce((p, n) =>
          p + n.peak,
          0
        ) ?? 0
      }

      onPreview = queue.raf(() => {
        // await new Promise(requestAnimationFrame)
        // if (signal.aborted) {
        //   console.log('aborted')
        //   return
        // }
        if ($.shouldDraw) {
          // console.log('RUN DRAW')
          $.shouldDraw = false
          // $.visible.clear()
          this.draw()
          this.calcRmsSum()
        }
      })

      draw = () => {
        this.plot()
        $.editor!.$.drawDeco()
      }
    }));
  },
  function effects({ $, fx, deps, refs }) {
    fx(() => skin.fx(({ fonts, colors }) => {
      $.css = /*css*/`
      & {
        display: flex;
      }

      ${Editor} {
        width: 100%;
        height: 100%;
      }
      `
    }))

    fx(() => {
      anim.$.entities = [
        ...anim.$.entities,
        $.calcRms,
        $.calcRmsSum,
        $.draw
      ]
      return () => {
        anim.$.entities = anim.$.entities.filter(
          (fn) =>
            fn !== $.draw
            && fn !== $.calcRms
            && fn !== $.calcRmsSum
        )
      }
    })

    fx(() => audio.fx(({ dspl, analyser }) => {
      $.dspl = dspl
      $.analyser = analyser
    }))

    fx(({ dspl }) => {
      dspl.onPreview = $.onPreview
    })

    fx(({ dspl }) => {
      $.rms = createRms(dspl)
    })

    fx(({ editor, genDeco, rmsDeco, rangeDeco }) => {
      editor.$.deco = [...genDeco, ...rmsDeco, ...rangeDeco]
    })

    fx(({ editor, rangeSubs, errorSubs, debugSubs }) => {
      editor.$.sub = [...rangeSubs, ...errorSubs, ...debugSubs]
    })

    fx.task(({ editor, tokens }) => {
      editor.$.tokens = tokens
      if ($.focusedAudios.length) {
        $.focusedAudios = []
      }
    })

    fx(({ code }) => {
      // console.time('tokenize')
      $.tokens = [...tokenize(code)]
      // console.timeEnd('tokenize')
      localStorage.codeValue2 = code
    })

    fx(({ editor: _, tokens, dspl }) => {
      try {
        const cst = parse(tokens)
        if ($.info) {
          const diff = differ($.info.cst, cst)

          if (
            diff.changed.length
            + diff.moved.length
            + diff.removed.length
            + diff.added.length
            === 0
            && diff.literals.length
          ) {
            // if (diff.literals.length) {
            // quick path

            // TODO: update literals
            diff.literals.forEach(([p, n]) => {
              p.text = n.text
              const ptr = p.meta.param.target.ptr

              // value changed by the slider
              dspl.frontend.literals[dspl.frontend.literalsCurrent][ptr] = parseNumber(n.text).value
              // console.log(p, n)
            })
            // }


            // TODO: merge all new line col for all equal tokens

            return
          }

          // console.log('DIFF', diff)
        }

        $.info = produce(cst)

        // if ($.errors?.length) {
        //   genRefMap.clear()
        //   $.focusedAudios = []
        // }

        $.errors = []
      }
      catch (e) {
        const error = e as Error
        console.warn(error)
        console.warn(error.cause)

        // genRefMap.clear()
        // $.focusedAudios = []

        // editor.$.tokens = tokens
        $.errors = [error as any]
      }
    })

    fx.raf(async ({ dspl: _d, rms: _r, info: _i, live }) => {
      if (!live && $.build && !$.isHandlingRange) return
      // console.time('compile')
      await $.compile()
      // console.timeEnd('compile')
    })

    const pickMarker = new PickMarker()

    fx(({ picks, dspl }) => {
      $.pickMarkers = picks.flatMap((scalar, scalarIndex) =>
        scalar.tokens.map((x, index) => ({
          line: x.line,
          start: x.col,
          end: x.right,
          meta: { index, scalar, scalarIndex, dspl },
          draw: pickMarker.draw,
        })))
    })

    const errorMarker = new ErrorMarker()

    fx(({ errors }) => {
      const nodes: [CstNode, Error][] = []

      for (const error of errors) {
        const errorNodes = [...(error.cause?.nodes ?? [])]
        let node: CstNode | undefined
        while (errorNodes.length) {
          node = errorNodes.pop()!
          if ('line' in node
            && !preludeTokens.has(node)
            && !node.tokens?.some((node) =>
              preludeTokens.has(node))
          ) {
            nodes.push([node, error])
            break
          }
        }
      }

      const dims = nodes.map(([node, error]): Dim => ({
        line: node.line,
        start: node.col,
        end: node.right,
        meta: error
      }))

      $.errorMarkers = dims.map((x) => ({
        ...x,
        draw: errorMarker.draw
      }))

      $.errorSubs = dims.map((x) => ({
        ...x,
        height: 15,
        draw: Object.assign(new ErrorSub(), { message: x.meta.message }).draw
      }))
    })

    fx(({ editor, errorMarkers, pickMarkers }) => {
      editor.$.markers = [...pickMarkers, ...errorMarkers]
    })

    const HEIGHT = 37

    // TODO: make the dim/node dimensions the same properties
    // function nodePosId({ line, col, right }: { line: number, col: number, right: number }) {
    //   return `${line}-${col}-${right}`
    // }

    function makeDim<T>(view: { line: number; col: number; right: number, bottom: number }, meta: T): Dim<T> {
      const dim: Dim<T> = {
        line: view.line,
        start: view.col,
        end: view.right,
        bottom: view.bottom,
        meta
      }
      return dim
    }

    function makeRmsDim(dim: Dim<VAudio>) {
      const rmsDim: Dim<VAudio> = {
        line: dim.bottom ?? dim.line,
        start: dim.end - 1,
        end: dim.end,
        meta: dim.meta!
      }
      return rmsDim
    }

    fx(({ elevated, editor }) => {
      editor.$.elevated = elevated
    })

    fx(({ build, visible, brackets }) => {
      const { info } = build

      const elevated = [...info.scope.gens.values()].flatMap((gen) =>
        [...(gen.audio?.callers ?? gen.callers)].filter((x) => x.type === 'scoped')
      )
      $.elevated = elevated

      $.picks = [...info.scope.scalars.values()].filter((s) => s.text === 'pick' || s.text === 'slide')

      const genDims: Map<string, Dim> = new Map()
      const rmsDims: Map<string, Dim> = new Map()
      const current = new Map<string, VAudio>()

      const keys = (view: Caller | VAudio, audio: VAudio) => [
        `${view.line}:${view.col}`,
        `${'key' in view ? view.key : view.meta.key ?? view.text}:${audio.ptr}`
      ]

      const views = <T extends (Caller | VAudio)>(arr: T[]): T[] =>
        arr.filter((x) => !x.meta.hidden)

      const audios =
        [...info.scope.audios.values()].filter((x) =>
          x.floats && x.gen?.kind !== 'zero' && x.text !== 'zero'
        )

      // console.groupCollapsed('current')
      for (const audio of audios) {
        if (!audio.callers.length) continue

        const [view] = audio.callers

        if (brackets && brackets[0]?.line === view.line && brackets[0]?.col === view.col) {
          for (const view of views([audio, ...audio.callers])) {
            const [, keyPtr] = keys(view, audio)
            // console.log(keyPtr, `${view.line}:${view.col}`, view)
            current.set(keyPtr, audio)
          }
        }
      }
      // console.groupEnd()

      const hadVisible = !!visible.size
      const newlyVisible = new Map<string, string>()
      const prevVisible = new Map<string, string>()

      for (const audio of current.values()) {
        // if (audio.callers.length <= 1) continue

        for (const view of views([audio, ...audio.callers])) {
          const dim = makeDim(view, audio)
          const [key, keyPtr] = keys(view, audio)

          if (audio.callers.length) genDims.set(key, dim)

          const rmsDim: Dim = makeRmsDim(dim)
          rmsDims.set(key, rmsDim)

          newlyVisible.set(key, keyPtr)
        }
      }

      // console.groupCollapsed('inferred')
      for (const audio of audios) {
        // if (audio.callers.length <= 1) continue
        for (const view of views([audio, ...audio.callers])) {
          const dim = makeDim(view, audio)
          const [key, keyPtr] = keys(view, audio)

          if (
            !hadVisible
            || (visible.has(keyPtr) && !newlyVisible.has(key))
          ) {
            if (audio.callers.length) genDims.set(key, dim)

            // if (view.line <= 2) console.log(keyPtr, `${view.line}:${view.col}`, view)
            const rmsDim: Dim = makeRmsDim(dim)

            rmsDims.set(key, rmsDim)

            prevVisible.set(key, keyPtr)
          }
        }
      }
      // console.groupEnd()



      // console.log([...genDims.entries()].map(([key, value]) => `${key} ${value.meta.ptr}`))
      // console.log({ genDims })
      // $.visible.clear()
      // if (newlyVisible.size) {
      $.nextVisible.clear()
      prevVisible.forEach((keyPtr, key) => {
        if (!newlyVisible.has(key)) $.nextVisible.add(keyPtr)
      })
      newlyVisible.forEach((keyPtr, key) => {
        $.nextVisible.add(keyPtr)
      })

      $.visibleHash = [...$.nextVisible.keys()].join(' ')
      // }
      $.genDims = [...genDims.values()].sort(sortDims)
      $.rmsDims = [...rmsDims.values()].sort(sortDims)

      const rangeDims: Dim<Param>[] = []
      for (const param of info.scope.params.values()) {
        const view = bounds(param.nodes as any)
        const dim = makeDim(view, param)
        rangeDims.push(dim)
      }

      $.rangeDims = rangeDims
      // const rangeDims = [...info.scope.params].map((p) => {
      //   // const view = nodesBounds(p.meta.nodes)
      //   return {
      //     line: view.line,
      //     start: view.col,
      //     end: view.right,
      //     meta: p
      //   }
      // })
    })

    fx(({ nextVisible, visibleHash: _vh }) => {
      $.shouldDraw = true
      $.visible.clear()
      nextVisible.forEach((key) => { $.visible.add(key) })
      $.onPreview()
    })

    fx(({ editor }) =>
      editor.fx(({ line, col, brackets }) => {
        $.line = line
        $.col = col
        $.brackets = brackets
      })
    )

    function sortDims(a: Dim, b: Dim) {
      return a.line === b.line
        ? a.start - b.start
        : a.line < b.line
          ? -1
          : 1
    }

    fx(({ genDims, rmsDims }) => {
      const lines: Dim[] = []
      const all = [...genDims, ...rmsDims].filter(Boolean)

      // console.log(...all)
      all.sort(sortDims)

      all.forEach((d) => {
        if (lines[d.line]) { //} && lines[d.line].meta !== d.meta) {
          lines[d.line].end = Math.min(
            lines[d.line].end,
            d.start
          )
        }
        lines[d.line] = d
      })

      // console.log('what', genDims)
    })

    const allPlots: Plot[] = []

    fx(({ analyser, genDims }) => {
      $.plots = genDims
        .map((d, i) =>
          allPlots[i] = Object.assign(
            $.plots?.[i] ?? allPlots[i] ?? new Plot(analyser),
            { meta: d }
          )
        )
    })

    fx(({ plots }) => app.fx(({ stabilize }) => {
      plots.forEach((plot) => {
        plot.stabilize = stabilize
      })
    }))

    fx(({ plots, genDims }) => {
      plots.forEach((p) => {
        if (!p.initial) return
        // console.log('DRAW', p.initial)
        p.initial = false
        p.resize(
          PLOT_WIDTH_PX,
          PLOT_HEIGHT_PX,
          screen.$.pr
        )
        const dim = p.meta!
        if (!dim) return

        const floats = dim.meta!.floats!
        // console.log(dim.meta.ptr)
        p.draw(floats)
      })

      $.genDeco = genDims.map((d, i): Deco => {
        const deco: Deco = {
          ...d,
          height: HEIGHT,
          center: false,
          preserveAspectRatio: false,
          canvas: plots[i].canvas
        }
        plots[i].deco = deco
        return deco
      })

      // if ($.shouldDraw) {
      //   $.shouldDraw = false
      //   $.draw()
      // }
      // requestAnimationFrame($.draw)
      // $.draw()
    })

    fx(({ rmsDims }) => {
      $.rmss = rmsDims
        .map((_, i) =>
          $.rmss?.[i] ?? new Rms()
        )
    })

    fx(({ rmss, rmsDims, dspl }) => {
      rmss.forEach((rms, i) => {
        rmsDims[i]!.meta!.meta!.rms = rms
        rms.index = rmsDims[i]!.meta!.ptr
        rms.floats = dspl.frontend.rms
      })

      $.rmsDeco = rmsDims.map((r, i): Deco => ({
        ...r,
        height: HEIGHT,
        center: false,
        preserveAspectRatio: false,
        update: rmss[i].update,
        draw: rmss[i].draw
      }))

      $.calcRmsSum()
    })

    fx(({ rangeDims }) => {
      $.ranges = rangeDims
        .map((d, index) =>
          Object.assign($.ranges?.[index] ?? new Knob(), { index })
        )
    })

    const allRangePlots: Plot[] = []

    fx(({ editor, ranges, rangeDims, analyser }) => {

      if ($.isHandlingRange) return

      // if (!ranges.length) return

      let hasChanges = false

      const hashParam = (p: Param) => {
        return [
          p.name?.text,
          p.mute,
          p.solo,
          p.min,
          p.max,
          p.slope,
          p.target.digits,
          p.target.format,
          p.op.index,
        ].join()
      }

      ranges.forEach((k, i) => {
        const dim = rangeDims[i]
        if (!k.meta || hashParam(dim.meta!) !== hashParam(k.meta!.meta!)) {
          hasChanges = true
        }
        k.meta = dim
        const { min, max, step, slope } = dim.meta!
        const current = dim.meta!.target
        k.min = min
        k.max = max
        k.step = step
        k.slope = slope
        k.value = clamp(0, 1,
          ((current.value - k.min) / (k.max - k.min))
          ** (1 / k.slope)
        )
        // console.log('yo', p.value)
      })

      // // TODO: consolidate range/param in new Knob() ?
      const namedRanges = ranges.filter((k) =>
        k.meta!.meta!.name
      )

      // // // console.log(namedRanges)


      // try {
      if (hasChanges) {
        $.namedRanges = namedRanges
      }
      else {
        return
      }
      // }
      // finally {
      //   ranges.forEach((k, i) => {
      //     k.meta = rangeDims[i]
      //   })
      // }
      // .map((k) =>
      // // $.namedRanges?.[i] && $.namedRanges[i].param.name!.text === rangeDims[range.index].meta!.name!.text
      // //   ? $.namedRanges[i] :
      // ({
      //   range: k,
      //   param: k.meta!.meta!,
      // })
      // )

      const plotRanges = namedRanges
        .filter((k) => k.meta!.meta!.last)

      const rangePlots = $.rangePlots = plotRanges
        .map((k, i) =>
          allRangePlots[i] = Object.assign(
            $.rangePlots?.[i] ?? allRangePlots[i] ?? new Plot(analyser),
            { meta: k.meta }
          )
        )

      rangePlots.forEach((p) => {
        if (!p.initial) return
        // console.log('DRAW', p.initial)
        p.initial = false
        p.resize(
          PLOT_WIDTH_PX,
          PLOT_HEIGHT_PX,
          screen.$.pr
        )
        const dim = p.meta!
        if (!dim) return

        const floats = dim.meta.last.floats!
        // console.log(dim.meta.ptr)
        p.draw(floats)
      })

      $.rangeDeco = plotRanges
        .map((k, i): Deco => {
          const deco: Deco = {
            ...k.meta!,
            start: k.meta!.start + 1,
            height: HEIGHT,
            center: false,
            preserveAspectRatio: false,
            canvas: rangePlots[i].canvas
          }
          rangePlots[i].deco = deco
          return deco
        })

      // plotRanges.forEach(({ range, param }, i) => {
      //   const dim = rangeDims[range.index]
      //   dim.floats = param.last.floats
      // })

      $.rangeSubs = rangeDims.map((d, i): Sub => ({
        ...d,
        height: IS_MOBILE ? 25 : 15,
        // center: true,
        // preserveAspectRatio: true,
        // canvas: ranges[i].canvas
        onPointerDown: $.handleRangeDown(i),
        onPointerMove: $.handleRangeMove(i),
        onPointerLeave: $.handleRangeLeave(i),
        draw: ranges[i].draw,
      }))
    })

    fx(({ debugDims }) => {
      $.debugSubs = debugDims.map((d, i): Sub => ({
        ...d,
        height: 15,
        draw: debugDraw,
      }))
    })

    // fx(({ namedRanges }) => {
    //   console.log('named ranges:', namedRanges)
    // })

    fx(({ name }) => {
      $.view = <>
        <Editor
          ref={refs.editor}
          rect={new Rect(0, 0, window.innerWidth, window.innerHeight)}
          name={name}
          value={deps.code}
          onKeyDown={({ key, ctrlKey, metaKey }) => {
            if (key === 'Enter' && (ctrlKey || metaKey)) {
              $.compile()
              return true
            }
            return false
          }}
        />
      </>
    })
  }
)))
